import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calculator, MapPinned, Plus, Trash2, AlertTriangle, PackagePlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Cliente", "Mão de obra", "Deslocamento", "Peças", "Margem", "Resultado"];

export default function QuoteCalculator() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [services, setServices] = useState([]);
  const [catalogParts, setCatalogParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [calc, setCalc] = useState(null);
  const [refResult, setRefResult] = useState(null);
  const [refQuery, setRefQuery] = useState({ category: "hora_tecnica", capacity: "" });
  const [saving, setSaving] = useState(false);
  const [costCtx, setCostCtx] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const simTimer = useRef(null);

  const s = company.settings || {};
  const [form, setForm] = useState({
    customer_id: "", equipment_id: "", service_type: "", description: "",
    inputs: {
      horas: 1, tecnicos: s.num_tecnicos || 1, valor_hora: s.valor_hora || 120,
      distancia_km: 0, valor_km: s.valor_km || 2.5, outros_custos: 0,
      impostos_pct: s.impostos_pct ?? 6, margem_pct: s.margem_pct ?? 30,
      desconto: 0, desconto_tipo: "valor", urgencia: "normal",
    },
    parts: [], market_ref: null, validity_days: 15, warranty: "90 dias",
    payment_terms: "50% de entrada, restante na conclusão", notes: "",
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/parts").then((r) => setCatalogParts(r.data)).catch(() => {});
    api.get("/market/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/costs/pricing-context").then((r) => {
      setCostCtx(r.data);
      if (r.data.configured) {
        setForm((f) => ({
          ...f,
          inputs: {
            ...f.inputs,
            valor_hora: r.data.valor_hora_sugerido || f.inputs.valor_hora,
            valor_km: r.data.valor_km_sugerido || f.inputs.valor_km,
            impostos_pct: r.data.impostos_pct ?? f.inputs.impostos_pct,
            margem_pct: r.data.margem_pct ?? f.inputs.margem_pct,
          },
        }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.customer_id) {
      api.get(`/customers/${form.customer_id}`).then((r) => setEquipment(r.data.equipment || [])).catch(() => {});
    } else {
      setEquipment([]);
    }
  }, [form.customer_id]);

  const setInput = (k, v) => setForm({ ...form, inputs: { ...form.inputs, [k]: v } });
  const setNumInput = (k) => (e) => setInput(k, Number(e.target.value) || 0);

  const addPart = (p) => {
    setForm({ ...form, parts: [...form.parts, { part_id: p.id || null, name: p.name, qty: 1, cost: p.cost || 0 }] });
  };

  const importKit = () => {
    try {
      const kit = JSON.parse(localStorage.getItem("gepr_kit") || "[]");
      if (!kit.length) return;
      setForm({ ...form, parts: [...form.parts, ...kit.map((k) => ({ part_id: null, name: k.name, qty: k.qty, cost: k.cost || 0 }))] });
      localStorage.removeItem("gepr_kit");
      toast.success(`${kit.length} itens do kit adicionados. Revise os custos unitários.`);
    } catch {
      toast.error("Não foi possível importar o kit.");
    }
  };

  const updatePart = (i, k, v) => {
    const parts = [...form.parts];
    parts[i] = { ...parts[i], [k]: k === "name" ? v : Number(v) || 0 };
    setForm({ ...form, parts });
  };

  const payload = useMemo(() => ({
    customer_id: form.customer_id || "00000000-0000-0000-0000-000000000000",
    equipment_id: form.equipment_id, service_type: form.service_type || "Serviço",
    description: form.description, inputs: form.inputs, parts: form.parts,
    market_ref: form.market_ref, validity_days: form.validity_days,
    warranty: form.warranty, payment_terms: form.payment_terms, notes: form.notes,
  }), [form]);

  const doCalc = async () => {
    try {
      const { data } = await api.post("/quotes/calculate", payload);
      setCalc(data);
      setStep(5);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const recalc = (inputs) => {
    clearTimeout(simTimer.current);
    simTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/quotes/calculate", { ...payload, inputs });
        setCalc(data);
      } catch { /* mantém o último cálculo válido */ }
    }, 400);
  };

  const simSet = (k, v) => {
    const inputs = { ...form.inputs, [k]: v };
    setForm({ ...form, inputs });
    recalc(inputs);
  };

  const consultRef = async () => {
    if (!company.city || !company.state) {
      toast.error("Complete cidade e estado da empresa em Configurações para consultar referências.");
      return;
    }
    try {
      const { data } = await api.get("/market/reference", {
        params: { state: company.state, city: company.city, category: refQuery.category, capacity: refQuery.capacity },
      });
      setRefResult(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyRef = () => {
    if (!refResult || refResult.forming) return;
    const median = refResult.median;
    if (refQuery.category === "hora_tecnica") setInput("valor_hora", median);
    else if (refQuery.category === "km_deslocamento") setInput("valor_km", median);
    setForm((f) => ({
      ...f,
      market_ref: {
        category: refQuery.category, category_name: refResult.category?.name,
        range_label: `${brl(refResult.range[0])} a ${brl(refResult.range[1])}`,
        median, count: refResult.count, updated_label: refResult.updated_at,
      },
    }));
    api.post("/events", { event: "referencia_aplicada", data: { category: refQuery.category } }).catch(() => {});
    toast.success("Referência aplicada como sugestão. Revise os valores antes de salvar.");
  };

  const save = async () => {
    if (!form.customer_id) {
      toast.error("Selecione um cliente na etapa 1.");
      setStep(0);
      return;
    }
    setSaving(true);
    try {
      const finalPayload = Number(customPrice) > 0
        ? { ...payload, inputs: { ...payload.inputs, preco_final_custom: Number(customPrice) } }
        : payload;
      const { data } = await api.post("/quotes", finalPayload);
      toast.success(`Orçamento ${data.number} criado!`);
      navigate(`/app/orcamentos/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === 0 && (!form.customer_id || !form.service_type)) {
      toast.error("Selecione o cliente e o tipo de serviço.");
      return;
    }
    if (step === 4) return doCalc();
    setStep(step + 1);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-in-up" data-testid="quote-calculator">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Calculadora de orçamento</h1>
        <p className="text-sm text-slate-500">Passo {step + 1} de {STEPS.length} · {STEPS[step]}</p>
      </div>
      <div className="flex gap-1.5">
        {STEPS.map((st, i) => (
          <div key={st} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-sky-600" : "bg-slate-200"}`} />
        ))}
      </div>
      {costCtx && !costCtx.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center justify-between gap-3" data-testid="qc-costs-banner">
          <span>Preencha as informações básicas dos seus custos operacionais para que o GEPR calcule uma precificação adequada à realidade da sua empresa.</span>
          <Button size="sm" variant="outline" className="h-9 shrink-0 border-amber-300 text-amber-800" onClick={() => navigate("/app/custos")} data-testid="qc-costs-banner-btn">Preencher agora</Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
        {step === 0 && (
          <>
            <div className="space-y-1.5">
              <Label>Cliente *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, equipment_id: "" })}>
                <SelectTrigger className="h-12" data-testid="qc-customer"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {customers.length === 0 && (
                <p className="text-xs text-amber-700">Nenhum cliente ainda. Cadastre em Clientes antes de continuar.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Equipamento</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })} disabled={!equipment.length}>
                <SelectTrigger className="h-12" data-testid="qc-equipment"><SelectValue placeholder={equipment.length ? "Selecione" : "Cadastre equipamentos no cliente"} /></SelectTrigger>
                <SelectContent>
                  {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{[e.type, e.brand, e.model].filter(Boolean).join(" · ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de serviço *</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="h-12" data-testid="qc-service"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                <SelectContent>
                  {services.map((sv) => <SelectItem key={sv.id} value={sv.name}>{sv.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descrição do serviço</Label>
              <Textarea data-testid="qc-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Ex: Troca de compressor e carga de refrigerante..." rows={3} />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Horas de trabalho</Label>
                <Input className="h-12 font-mono-num" type="number" step="0.5" data-testid="qc-horas" value={form.inputs.horas} onChange={setNumInput("horas")} />
              </div>
              <div className="space-y-1.5">
                <Label>Nº de técnicos</Label>
                <Input className="h-12 font-mono-num" type="number" min="1" data-testid="qc-tecnicos" value={form.inputs.tecnicos} onChange={setNumInput("tecnicos")} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Valor da hora técnica (R$)</Label>
              <Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="qc-valor-hora" value={form.inputs.valor_hora} onChange={setNumInput("valor_hora")} />
              {costCtx?.configured ? (
                <p className="text-xs text-sky-700" data-testid="qc-hora-custo-hint">
                  Do seu custo operacional: custo {brl(costCtx.custo_hora || 0)}/h → preço sugerido {brl(costCtx.valor_hora_sugerido || 0)}/h (custo + impostos + margem). Você pode alterar.
                </p>
              ) : (
                <p className="text-xs text-slate-500">Padrão da sua configuração: {brl(s.valor_hora || 0)}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Urgência</Label>
              <Select value={form.inputs.urgencia} onValueChange={(v) => setInput("urgencia", v)}>
                <SelectTrigger className="h-12" data-testid="qc-urgencia"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgente">Urgente (+{s.adicional_urgencia ?? 30}%)</SelectItem>
                  <SelectItem value="plantao">Plantão / noturno (+{s.adicional_noturno ?? 50}%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Distância total (km)</Label>
                <Input className="h-12 font-mono-num" type="number" step="0.1" data-testid="qc-km" value={form.inputs.distancia_km} onChange={setNumInput("distancia_km")} />
              </div>
              <div className="space-y-1.5">
                <Label>Valor por km (R$)</Label>
                <Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="qc-valor-km" value={form.inputs.valor_km} onChange={setNumInput("valor_km")} />
              </div>
            </div>
            <p className="text-xs text-slate-500">Deslocamento mínimo configurado: {brl(s.deslocamento_minimo || 0)}</p>
            <div className="space-y-1.5">
              <Label>Outros custos (R$)</Label>
              <Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="qc-outros" value={form.inputs.outros_custos} onChange={setNumInput("outros_custos")} />
              <p className="text-xs text-slate-500">Pedágio, estacionamento, ajudante, aluguel de equipamento...</p>
              {costCtx?.variable_costs?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="qc-varcost-chips">
                  <span className="text-[11px] text-slate-400 w-full">Dos seus custos variáveis cadastrados (toque para somar):</span>
                  {costCtx.variable_costs.map((c, i) => (
                    <button key={i} type="button"
                      className="text-xs border border-slate-300 rounded-full px-2.5 py-1 hover:bg-slate-50 font-mono-num"
                      data-testid={`qc-varcost-${i}`}
                      onClick={() => setInput("outros_custos", (Number(form.inputs.outros_custos) || 0) + (Number(c.value) || 0))}>
                      + {c.description || "Custo variável"} {brl(c.value)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="space-y-1.5">
              <Label>Adicionar peça do catálogo</Label>
              <Select onValueChange={(id) => { const p = catalogParts.find((x) => x.id === id); if (p) addPart(p); }}>
                <SelectTrigger className="h-12" data-testid="qc-add-part"><SelectValue placeholder="Buscar no banco de peças..." /></SelectTrigger>
                <SelectContent>
                  {catalogParts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {brl(p.cost)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" className="h-11 w-full" onClick={() => addPart({ name: "", cost: 0 })} data-testid="qc-add-manual-part">
                <Plus className="h-4 w-4 mr-1" /> Adicionar peça manual
              </Button>
              {localStorage.getItem("gepr_kit") && (
                <Button type="button" variant="outline" className="h-11 w-full border-sky-300 text-sky-700 hover:bg-sky-50" onClick={importKit} data-testid="qc-import-kit">
                  <PackagePlus className="h-4 w-4 mr-1" /> Importar kit de instalação
                </Button>
              )}
            </div>
            {form.parts.length === 0 && <p className="text-sm text-slate-500">Nenhuma peça adicionada.</p>}
            {form.parts.map((p, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`qc-part-${i}`}>
                <div className="flex gap-2">
                  <Input className="h-11 flex-1" placeholder="Nome da peça" value={p.name} onChange={(e) => updatePart(i, "name", e.target.value)} data-testid={`qc-part-name-${i}`} />
                  <Button type="button" variant="ghost" size="icon" className="h-11 w-11 text-red-500" onClick={() => setForm({ ...form, parts: form.parts.filter((_, x) => x !== i) })} data-testid={`qc-part-del-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input className="h-11 font-mono-num" type="number" min="1" placeholder="Qtd" value={p.qty} onChange={(e) => updatePart(i, "qty", e.target.value)} data-testid={`qc-part-qty-${i}`} />
                  <Input className="h-11 font-mono-num" type="number" step="0.01" placeholder="Custo unit." value={p.cost} onChange={(e) => updatePart(i, "cost", e.target.value)} data-testid={`qc-part-cost-${i}`} />
                </div>
              </div>
            ))}
          </>
        )}

        {step === 4 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Impostos (%)</Label>
                <Input className="h-12 font-mono-num" type="number" step="0.1" data-testid="qc-impostos" value={form.inputs.impostos_pct} onChange={setNumInput("impostos_pct")} />
              </div>
              <div className="space-y-1.5">
                <Label>Margem de lucro (%)</Label>
                <Input className="h-12 font-mono-num" type="number" step="1" data-testid="qc-margem" value={form.inputs.margem_pct} onChange={setNumInput("margem_pct")} />
                {costCtx?.refs?.margem && (
                  <p className="text-xs text-slate-500" data-testid="qc-margem-ref">
                    Sugestão GEPR: {costCtx.refs.margem.value}% (faixa {costCtx.refs.margem.value_min}–{costCtx.refs.margem.value_max}%) — referência inicial para serviços técnicos no Brasil. Você pode alterar conforme sua estratégia.
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desconto</Label>
                <Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="qc-desconto" value={form.inputs.desconto} onChange={setNumInput("desconto")} />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de desconto</Label>
                <Select value={form.inputs.desconto_tipo} onValueChange={(v) => setInput("desconto_tipo", v)}>
                  <SelectTrigger className="h-12" data-testid="qc-desconto-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="valor">R$</SelectItem>
                    <SelectItem value="pct">%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-sky-200 bg-sky-50 rounded-lg p-4 space-y-3" data-testid="qc-market-box">
              <div className="flex items-center gap-2">
                <MapPinned className="h-4 w-4 text-sky-700" />
                <p className="text-sm font-semibold text-sky-900">Referência regional (opcional · beta)</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={refQuery.category} onValueChange={(v) => setRefQuery({ ...refQuery, category: v, capacity: "" })}>
                  <SelectTrigger className="h-11 bg-white" data-testid="qc-ref-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(categories.find((c) => c.id === refQuery.category)?.capacities || []).length > 0 ? (
                  <Select value={refQuery.capacity} onValueChange={(v) => setRefQuery({ ...refQuery, capacity: v })}>
                    <SelectTrigger className="h-11 bg-white" data-testid="qc-ref-capacity"><SelectValue placeholder="BTUs" /></SelectTrigger>
                    <SelectContent>
                      {(categories.find((c) => c.id === refQuery.category)?.capacities || []).map((cap) => (
                        <SelectItem key={cap} value={cap}>{cap} BTUs</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : <div />}
              </div>
              <Button type="button" variant="outline" className="h-11 w-full bg-white" onClick={consultRef} data-testid="qc-ref-consult-btn">
                Consultar referência em {company.city || "sua cidade"}/{company.state || "UF"}
              </Button>
              {refResult && (
                <div className="text-sm space-y-2" data-testid="qc-ref-result">
                  {refResult.forming ? (
                    <p className="text-amber-800">{refResult.message} Piso padrão: <b className="font-mono-num">{brl(refResult.piso_padrao)}</b> ({refResult.count}/{refResult.min_contributions} contribuições)</p>
                  ) : (
                    <>
                      <p className="text-slate-800">
                        Referência regional: <b className="font-mono-num">{brl(refResult.range[0])} a {brl(refResult.range[1])}</b>
                        {" · "}mediana <b className="font-mono-num">{brl(refResult.median)}</b>
                      </p>
                      <p className="text-xs text-slate-500">
                        Baseada em {refResult.count} contribuições em {company.city}, atualizada em {refResult.updated_at}.
                        {" "}Confiança: {refResult.confidence}.{refResult.demo ? " (dados de demonstração)" : ""}
                      </p>
                      <p className="text-xs text-slate-500 italic">{refResult.disclaimer}</p>
                      <Button type="button" className="h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={applyRef} data-testid="qc-ref-apply-btn">
                        Usar como sugestão
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Validade (dias)</Label>
                <Input className="h-12 font-mono-num" type="number" data-testid="qc-validade" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: Number(e.target.value) || 15 })} />
              </div>
              <div className="space-y-1.5">
                <Label>Garantia</Label>
                <Input className="h-12" data-testid="qc-garantia" value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Condições de pagamento</Label>
              <Input className="h-12" data-testid="qc-pagamento" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
            </div>
          </>
        )}

        {step === 5 && calc && (
          <div className="space-y-4" data-testid="qc-result">
            <h2 className="text-lg font-bold">Resultado do cálculo</h2>
            {calc.alertas.map((a, i) => (
              <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-800" data-testid={`qc-alert-${i}`}>
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {a}
              </div>
            ))}
            <div className="font-mono-num text-sm space-y-1.5">
              {[
                ["Mão de obra", calc.custo_mao_obra], ["Deslocamento", calc.custo_deslocamento],
                ["Peças", calc.custo_pecas], ["Outros custos", calc.outros_custos],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-slate-100 pb-1.5">
                  <span className="text-slate-600">{l}</span><span>{brl(v)}</span>
                </div>
              ))}
              <div className="flex justify-between border-b border-slate-200 pb-1.5 font-semibold">
                <span>Custo total</span><span>{brl(calc.custo_total)}</span>
              </div>
              {calc.fator_urgencia > 1 && (
                <div className="flex justify-between pb-1.5"><span className="text-slate-600">Adicional de urgência</span><span>×{calc.fator_urgencia.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-600">Impostos</span><span>{brl(calc.impostos)}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-600">Preço recomendado</span><span>{brl(calc.preco_recomendado)}</span>
              </div>
              {calc.desconto > 0 && (
                <div className="flex justify-between border-b border-slate-100 pb-1.5 text-green-700">
                  <span>Desconto</span><span>- {brl(calc.desconto)}</span>
                </div>
              )}
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-600">Preço mínimo (sem prejuízo)</span><span>{brl(calc.preco_minimo)}</span>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-4 space-y-2" data-testid="qc-price-formation">
              <p className="text-sm font-semibold text-slate-800">Formação do preço</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Mercado</p>
                  <p className="font-mono-num font-semibold text-sm mt-1">{form.market_ref ? form.market_ref.range_label : "—"}</p>
                  {!form.market_ref && <p className="text-[10px] text-slate-400 mt-0.5">consulte a referência regional no passo anterior</p>}
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Seu custo</p>
                  <p className="font-mono-num font-semibold text-sm mt-1">{brl(calc.preco_minimo)}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">abaixo disso, prejuízo</p>
                </div>
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Sugestão GEPR</p>
                  <p className="font-mono-num font-semibold text-sm mt-1 text-sky-700">{brl(calc.preco_recomendado)}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">margem {calc.margem_real}% estimada</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-500">Você define o preço final. A sugestão considera seus custos{costCtx?.configured ? " operacionais reais" : ""}, impostos e a margem configurada — não é uma obrigação.</p>
            </div>
            <div className="bg-slate-900 text-white rounded-lg p-5 grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Preço final</p>
                <p className="font-mono-num text-lg sm:text-xl font-semibold text-sky-400" data-testid="qc-preco-final">{brl(calc.preco_final)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Lucro estimado</p>
                <p className="font-mono-num text-lg sm:text-xl font-semibold text-green-400" data-testid="qc-lucro">{brl(calc.lucro_estimado)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Margem real</p>
                <p className="font-mono-num text-lg sm:text-xl font-semibold" data-testid="qc-margem-real">{calc.margem_real}%</p>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="qc-simulator">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Calculator className="h-4 w-4 text-sky-600" /> Simular preço</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Horas</Label>
                  <Input className="h-10 font-mono-num" type="number" step="0.5" data-testid="qc-sim-horas" value={form.inputs.horas} onChange={(e) => simSet("horas", Number(e.target.value) || 0)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Distância (km)</Label>
                  <Input className="h-10 font-mono-num" type="number" step="0.1" data-testid="qc-sim-km" value={form.inputs.distancia_km} onChange={(e) => simSet("distancia_km", Number(e.target.value) || 0)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Margem (%)</Label>
                  <Input className="h-10 font-mono-num" type="number" step="1" data-testid="qc-sim-margem" value={form.inputs.margem_pct} onChange={(e) => simSet("margem_pct", Number(e.target.value) || 0)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desconto (R$)</Label>
                  <Input className="h-10 font-mono-num" type="number" step="0.01" data-testid="qc-sim-desconto" value={form.inputs.desconto} onChange={(e) => simSet("desconto", Number(e.target.value) || 0)} />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Altere e veja preço, lucro e margem atualizando em tempo real acima.</p>
            </div>
            {(() => {
              const eff = Number(customPrice) > 0 ? Number(customPrice) : calc.preco_final;
              const lucroEff = eff - calc.base - calc.impostos;
              const margemEff = eff > 0 ? (lucroEff / eff) * 100 : 0;
              const abaixo = calc.preco_minimo > 0 && eff < calc.preco_minimo;
              return (
                <div className="border border-sky-300 bg-sky-50 rounded-lg p-4 space-y-3" data-testid="qc-decision">
                  <p className="text-sm font-semibold text-sky-900">Sua decisão — quanto você quer cobrar?</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Input className="h-12 font-mono-num bg-white max-w-44" type="number" step="0.01" min="0"
                      placeholder={String(calc.preco_recomendado)} data-testid="qc-custom-price"
                      value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} />
                    {Number(customPrice) > 0 && (
                      <p className="text-xs text-sky-900" data-testid="qc-decision-metrics">
                        Lucro estimado <b className="font-mono-num">{brl(lucroEff)}</b> · margem <b className="font-mono-num">{margemEff.toFixed(1)}%</b>
                      </p>
                    )}
                  </div>
                  {abaixo && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 space-y-1" data-testid="qc-below-cost">
                      <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Atenção: este preço está abaixo do custo estimado da sua operação.</p>
                      <p className="font-mono-num">Custo estimado: {brl(calc.preco_minimo)} · Preço informado: {brl(eff)} · Diferença: {brl(calc.preco_minimo - eff)} ({((calc.preco_minimo - eff) / calc.preco_minimo * 100).toFixed(1)}% abaixo)</p>
                      <p>Abaixo deste valor, sua operação poderá não cobrir os custos estimados. A decisão é sua — o GEPR não bloqueia.</p>
                    </div>
                  )}
                  <p className="text-[11px] text-sky-800">Você define o preço final. Deixando em branco, usamos o preço calculado ({brl(calc.preco_final)}).</p>
                </div>
              );
            })()}
            {form.market_ref && (
              <p className="text-xs text-slate-500 italic">
                Referência regional registrada: {form.market_ref.category_name} — {form.market_ref.range_label}
                ({form.market_ref.count} contribuições, {form.market_ref.updated_label}). Referência informativa.
              </p>
            )}
            <Button className="w-full h-13 h-14 text-base bg-sky-600 hover:bg-sky-700 text-white active:scale-[0.98]" onClick={save} disabled={saving} data-testid="qc-save-quote-btn">
              {saving ? "Salvando..." : "Gerar orçamento"}
            </Button>
          </div>
        )}

        {step < 5 && (
          <div className="flex gap-3 pt-2">
            {step > 0 && (
              <Button variant="outline" className="h-12" onClick={() => setStep(step - 1)} data-testid="qc-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
              </Button>
            )}
            <Button className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={next} data-testid="qc-next-btn">
              {step === 4 ? <><Calculator className="h-4 w-4 mr-2" /> Calcular preço</> : <>Continuar <ArrowRight className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
        )}
        {step === 5 && (
          <Button variant="outline" className="w-full h-12" onClick={() => setStep(4)} data-testid="qc-adjust-btn">
            Ajustar valores
          </Button>
        )}
      </div>
    </div>
  );
}
